import { Injectable } from '@angular/core';
import { combineLatest, Observable, of } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, shareReplay, startWith, switchMap } from 'rxjs/operators';
import { DashboardFiltersFacade } from '../../domain/+state/dashboard-filters.facade';
import { DashboardFacade } from '../../domain/+state/dashboard.facade';
import { DashboardVmState } from '../../domain/model/dashboard-vm.model';
import { PipelineHealth, PipelineHealthDataVm, PipelineHealthVm } from '../../domain/model/pipeline-health.model';
import {
  clampToNow,
  DashboardSelection,
  errorVm,
  loadingVm,
  sameSelection,
  toSelection,
} from '../../shared/dashboard-vm.helper';
import {
  agingLegendLabel,
  higherIsBetterTier,
  toFunnelBarVm,
  toPipelineCardVm,
  zeroIsBestTier,
} from '../../shared/pipeline-funnel.helper';

const LOADING_VM: PipelineHealthVm = loadingVm<PipelineHealthDataVm>();

/** Everything the template reads is assembled here: no arithmetic and no string building in the markup. */
function toDataVm(health: PipelineHealth, now: Date): PipelineHealthVm {
  return {
    state: DashboardVmState.DATA,
    asOfUtc: clampToNow(health.asOfUtc, now),
    errorMessage: null,
    data: {
      bars: health.funnel.map((bar) => toFunnelBarVm(bar, health.agingThresholdDays)),
      // All three read the same absolute edge, blocked downwards and the other two upwards. The conversion
      // pair moves to `benchmarkTier` once an endpoint supplies a peer median.
      cards: [
        toPipelineCardVm(
          'blockedOnIncomplete',
          health.cards.blockedOnIncomplete,
          zeroIsBestTier(health.cards.blockedOnIncomplete.percent),
        ),
        toPipelineCardVm(
          'approvedVsSubmitted',
          health.cards.approvedVsSubmitted,
          higherIsBetterTier(health.cards.approvedVsSubmitted.percent),
        ),
        toPipelineCardVm(
          'fundedVsApproved',
          health.cards.fundedVsApproved,
          higherIsBetterTier(health.cards.fundedVsApproved.percent),
        ),
      ],
      agingThresholdDays: health.agingThresholdDays,
      legendLabel: agingLegendLabel(health.agingThresholdDays),
    },
  };
}

/**
 * Screen facade of the pipeline health page: turns the shared filters into one request per distinct
 * `(from, to, scope)` and a precomputed view model. Component-provided; lives as long as the screen.
 */
@Injectable()
export class PipelineHealthFacade {
  /** loading → data | error per request. */
  readonly vm$: Observable<PipelineHealthVm>;

  constructor(
    private readonly dashboardFacade: DashboardFacade,
    private readonly filtersFacade: DashboardFiltersFacade,
  ) {
    // `dateRange$` derives from `filters$` synchronously, so a period change emits twice; the wire tuple dedupes it.
    this.vm$ = combineLatest([this.filtersFacade.filters$, this.filtersFacade.dateRange$]).pipe(
      map(toSelection),
      filter((selection): selection is DashboardSelection => selection !== null),
      distinctUntilChanged(sameSelection),
      switchMap((selection: DashboardSelection) => this.load(selection)),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  /** Errors are caught here so one failed request never ends `vm$`; the next filter change fetches again. */
  private load(selection: DashboardSelection): Observable<PipelineHealthVm> {
    return combineLatest([
      this.dashboardFacade.getPipelineHealth$(selection.range, selection.scope),
      this.filtersFacade.now$,
    ]).pipe(
      map(([health, now]: [PipelineHealth, Date]) => toDataVm(health, now)),
      startWith(LOADING_VM),
      catchError((error: unknown) => of(errorVm<PipelineHealthDataVm>(error))),
    );
  }
}
